/**
 * Related Products — recommendations fetch + carousel / progress.
 */
(() => {
  const bindDragScroll = () => window.Theme?.bindDragScroll;

  class RelatedProductsSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-related-track]');
      this.prevBtn = this.querySelector('[data-related-prev]');
      this.nextBtn = this.querySelector('[data-related-next]');
      this.progressWrap = this.querySelector('[data-related-progress-wrap]');
      this.thumb = this.querySelector('[data-related-progress]');
      if (!this.track) return;

      this.items = () => [...this.track.querySelectorAll('.related-products__item')];

      this.onScroll = this.update.bind(this);
      this.onPrev = (event) => {
        event.preventDefault();
        this.go(-1);
      };
      this.onNext = (event) => {
        event.preventDefault();
        this.go(1);
      };
      this.onKeydown = (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.go(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(1);
        } else if (event.key === 'Home') {
          event.preventDefault();
          this.track.scrollTo({ left: 0, behavior: this.scrollBehavior() });
        } else if (event.key === 'End') {
          event.preventDefault();
          this.track.scrollTo({
            left: this.track.scrollWidth,
            behavior: this.scrollBehavior()
          });
        }
      };

      this.prevBtn?.addEventListener('click', this.onPrev);
      this.nextBtn?.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);

      const bind = bindDragScroll();
      if (typeof bind === 'function') bind(this.track);

      if (typeof ResizeObserver !== 'undefined') {
        this.ro = new ResizeObserver(() => this.update());
        this.ro.observe(this.track);
      }

      this.update();
      requestAnimationFrame(() => this.update());
    }

    disconnectedCallback() {
      this.prevBtn?.removeEventListener('click', this.onPrev);
      this.nextBtn?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.track?.removeEventListener('keydown', this.onKeydown);
      this.ro?.disconnect();
    }

    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    step() {
      const item = this.items()[0];
      if (!item || !this.track) return 0;
      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    go(direction) {
      const distance = this.step();
      if (!distance || !this.track) return;
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const target = Math.min(
        max,
        Math.max(0, Math.round(this.track.scrollLeft) + direction * distance)
      );
      const prevSnap = this.track.style.scrollSnapType;
      this.track.style.scrollSnapType = 'none';
      this.track.scrollTo({
        left: target,
        behavior: this.scrollBehavior()
      });
      window.setTimeout(() => {
        this.track.style.scrollSnapType = prevSnap;
        this.update();
      }, 320);
    }

    update() {
      if (!this.track) return;

      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const overflow = maxScroll > 2;
      this.classList.toggle('has-overflow', overflow);

      const canPrev = overflow && this.track.scrollLeft > 2;
      const canNext = overflow && this.track.scrollLeft < maxScroll - 2;

      if (this.prevBtn) {
        this.prevBtn.hidden = !overflow;
        this.prevBtn.disabled = !canPrev;
        this.prevBtn.setAttribute('aria-disabled', String(!canPrev));
      }
      if (this.nextBtn) {
        this.nextBtn.hidden = !overflow;
        this.nextBtn.disabled = !canNext;
        this.nextBtn.setAttribute('aria-disabled', String(!canNext));
      }

      if (this.progressWrap) {
        this.progressWrap.hidden = !overflow;
      }

      if (!this.thumb) return;

      if (!overflow) {
        this.thumb.style.width = '100%';
        this.thumb.style.left = '0%';
        return;
      }

      const ratio = Math.min(1, Math.max(0, this.track.scrollLeft / maxScroll));
      const thumbRatio = Math.min(1, this.track.clientWidth / this.track.scrollWidth);
      const thumbWidth = Math.max(thumbRatio * 100, 8);
      const travel = 100 - thumbWidth;
      this.thumb.style.width = `${thumbWidth}%`;
      this.thumb.style.left = `${ratio * travel}%`;
    }
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.url = this.dataset.url;
      if (!this.url) return;

      const hasItems = Boolean(
        this.querySelector('[data-related-track] .related-products__item, [data-recommendations-row] .cart-drawer__rec')
      );
      if (hasItems) {
        this.initNested();
        return;
      }

      const load = () => {
        if (this._fetched) return;
        this._fetched = true;
        fetch(this.url)
          .then((response) => response.text())
          .then((text) => {
            if (
              this.querySelector(
                '[data-related-track] .related-products__item, [data-recommendations-row] .cart-drawer__rec'
              )
            ) {
              this.initNested();
              return;
            }
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const intent = this.dataset.intent;
            const next =
              (intent &&
                doc.querySelector(`product-recommendations[data-intent="${intent}"]`)) ||
              doc.querySelector(`product-recommendations[data-section-id="${this.dataset.sectionId}"]`) ||
              doc.querySelector('product-recommendations');
            if (!next) {
              this.hideEmpty();
              return;
            }
            const html = next.innerHTML.trim();
            const track = next.querySelector('[data-related-track], [data-recommendations-row]');
            const hasTrack = Boolean(track && track.children.length);
            if (!html || !hasTrack) {
              this.hideEmpty();
              return;
            }
            this.innerHTML = html;
            this.hidden = false;
            this.initNested();
            if (this.closest('cart-drawer')) {
              this.querySelectorAll('.reveal, .product-card-reveal').forEach((el) => {
                el.classList.add('is-visible');
              });
            } else {
              window.Theme?.reveal?.scan?.(this);
            }
            if (window.Theme?.Wishlist?.sync) window.Theme.Wishlist.sync(this);
            this.closest('cart-drawer')?.syncRecsNav?.();
          })
          .catch(() => {
            this._fetched = false;
            if (
              this.querySelector(
                '[data-related-track] .related-products__item, [data-recommendations-row] .cart-drawer__rec'
              )
            ) {
              this.initNested();
              return;
            }
            this.hideEmpty();
          });
      };

      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(
          (entries) => {
            if (!entries[0]?.isIntersecting) return;
            this._io.disconnect();
            load();
          },
          { rootMargin: '0px 0px 80% 0px' }
        );
        this._io.observe(this);
      } else {
        load();
      }
    }

    disconnectedCallback() {
      this._io?.disconnect();
    }

    hideEmpty() {
      this.innerHTML = '';
      if (window.Shopify?.designMode) {
        this.hidden = false;
        const message = this.dataset.empty || 'No related products yet.';
        this.innerHTML = `<p class="related-products__empty">${message}</p>`;
        return;
      }

      const row = this.closest('[data-discovery-row]');
      if (row) {
        row.hidden = true;
        const root = row.closest('.related-products');
        if (root) {
          const rows = [...root.querySelectorAll('[data-discovery-row]')];
          if (rows.length && rows.every((node) => node.hidden)) {
            const section = root.closest('.shopify-section');
            if (section) section.style.display = 'none';
          }
        }
        return;
      }

      if (this.closest('cart-drawer, .cart-page')) {
        const recs = this.closest('.cart-drawer__recs');
        if (recs) recs.hidden = true;
        this.hidden = true;
        return;
      }

      const section = this.closest('.shopify-section');
      if (section) section.style.display = 'none';
      else this.hidden = true;
    }

    initNested() {
      const bind = bindDragScroll();
      this.querySelectorAll('[data-drag-scroll]').forEach((el) => {
        if (typeof bind === 'function') bind(el);
      });
      this.querySelectorAll('related-products-slider').forEach((el) => {
        if (typeof el.update === 'function') el.update();
      });
    }
  }

  if (!customElements.get('related-products-slider')) {
    customElements.define('related-products-slider', RelatedProductsSlider);
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-drag-scroll]').forEach((el) => {
      const bind = bindDragScroll();
      if (typeof bind === 'function') bind(el);
    });
    event.target.querySelectorAll('related-products-slider').forEach((el) => {
      if (typeof el.update === 'function') el.update();
    });
  });
})();
